import { Button, Checkbox } from "@mui/material";
import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import CustomModal, { ModalInput } from "./Modal";
import { submitInvoiceDetail, createChallanApi } from "../utils/api";
import { autoConnectAndPrint, buildChallanEscPos } from "../utils/bluetoothService";

const formatDescription = (description, dispatchNote) => {
  const parts = [];
  if (description) parts.push(description);
  if (dispatchNote) parts.push(dispatchNote);
  return parts.length > 0 ? parts.join(" - ") : "-";
};

const DispatchTable = ({
  data,
  complete,
  dispatchNote,
  setDispatchNote,
  type,
  refresh,
}) => {
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [selectedPrintOrderIds, setSelectedPrintOrderIds] = useState([]);
  const [printNote, setPrintNote] = useState("");
  const [weight, setWeight] = useState(0);
  const [invoiceLoading, setInvoiceLoading] = useState(false);

  // Photo Attachment state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const handleComplete = (orderIds, orders) => {
    setSelectedOrderIds(orderIds || []);
    setSelectedOrders(orders || []);
    setPhotoFile(null);
    setPhotoPreview(null);
    setShowCompleteModal(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const handlePrint = (orderIds) => {
    setSelectedPrintOrderIds(orderIds);
    setShowPrintModal(true);
  };

  const submitPrint = async (type) => {
    if (selectedPrintOrderIds.length > 0) {
      setInvoiceLoading(true);
      const [success, data] = await submitInvoiceDetail({
        orderIds: selectedPrintOrderIds,
        note: printNote,
        weight: weight,
      });
      if (success) {
        if (type) {
          toast.success("Invoice Created");
          if (data?._id) {
            window.open("/admin/invoice/" + data._id, "_blank");
          }
        } else {
          toast.success("Invoice Created");
          window.dispatchEvent(new Event("plabel-data-updated"));
          localStorage.setItem("plabel_last_sync", Date.now().toString());
        }
        setShowPrintModal(false);
        setPrintNote("");
        refresh?.();
      } else {
        toast.error(typeof data === "string" ? data : (data?.response?.data?.message || data?.message || "Failed to create invoice"));
      }
      setInvoiceLoading(false);
    }
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 text-slate-900 border-b border-amber-500">
              <th className="px-3 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Date
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                City
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Description
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Location
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Items
              </th>
              <th className="px-3 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Quantity
              </th>
              <th className="px-3 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                PhoneNumber
              </th>
              <th className="px-3 py-2.5 text-left text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Customer Name
              </th>
              <th className="px-3 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Challan
              </th>
              <th className="px-3 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider border-r border-amber-500 whitespace-nowrap">
                Image
              </th>
              <th className="px-3 py-2.5 text-center text-xs font-extrabold uppercase tracking-wider whitespace-nowrap bg-amber-400">
                <div className="flex items-center justify-center gap-1.5">
                  <span>Action</span>
                  <Link
                    to="/pdf/staff/dispatch"
                    target="_blank"
                    className="bg-red-600 hover:bg-red-700 active:bg-red-800 text-white text-[10px] font-black px-2 py-0.5 rounded shadow transition active:scale-95 inline-flex items-center gap-0.5"
                    title="Open Dispatch PDF"
                  >
                    <span>📄</span>
                    <span>PDF</span>
                  </Link>
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {data?.map((item, index) => {
              const orders = item?.orders?.length || 1;
              const isChallanCreated = item?.orders?.some(
                (o) => o?.isChallanCreated === true
              );
              return (
                <React.Fragment key={index}>
                  {item?.orders?.map((order, index2) => {
                    const isGroupBottom = index2 === orders - 1;
                    return (
                      <tr
                        key={index2}
                        className={`transition-colors ${
                          order?.isInvoiced
                            ? "bg-blue-50/70 hover:bg-blue-100/60"
                            : "hover:bg-slate-50/80"
                        } ${isGroupBottom ? "border-b-2 border-slate-300" : "border-b border-slate-100"}`}
                      >
                        {/* Date */}
                        <td className="px-3 py-2 text-xs text-gray-700 border-r border-slate-200 whitespace-nowrap">
                          <div className="font-semibold text-gray-800">
                            {new Date(order?.updatedAt).toLocaleDateString()}
                          </div>
                          <div className="text-[11px] text-gray-500">
                            {new Date(order?.updatedAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>
                        </td>

                        {/* City */}
                        <td className="px-3 py-2 text-xs font-semibold text-gray-700 border-r border-slate-200 whitespace-nowrap">
                          {order?.group || "-"}
                        </td>

                        {/* Description */}
                        <td className="px-3 py-2 text-xs text-gray-600 border-r border-slate-200">
                          {formatDescription(
                            order?.description,
                            order?.dispatchNote
                          )}
                        </td>

                        {/* Location */}
                        <td
                          className="px-3 py-2 text-xs text-gray-600 border-r border-slate-200 max-w-[150px] truncate"
                          title={order?.customer?.location}
                        >
                          {order?.customer?.location || "-"}
                        </td>

                        {/* Product Items */}
                        <td className="px-3 py-2 text-xs font-bold text-gray-900 border-r border-slate-200">
                          {order?.product?.productName || "-"}
                        </td>

                        {/* Quantity */}
                        <td className="px-3 py-2 text-xs font-bold text-center border-r border-slate-200">
                          <span className="bg-blue-50 text-blue-800 px-2 py-0.5 rounded font-mono font-bold">
                            {order?.quantity}
                          </span>
                        </td>

                        {/* Phone Number (Grouped) */}
                        {index2 === 0 && (
                          <td
                            rowSpan={orders}
                            className="px-3 py-2 text-xs font-mono font-bold text-gray-800 border-r border-slate-200 text-center whitespace-nowrap align-middle bg-white"
                          >
                            {item._id || "-"}
                          </td>
                        )}

                        {/* Customer Name */}
                        <td className="px-3 py-2 text-xs font-bold text-gray-900 border-r border-slate-200 align-middle">
                          <div className="font-bold text-slate-900 text-xs">
                            {order?.customer?.userName || "Customer"}
                          </div>
                        </td>

                        {/* Challan & Invoice (Grouped) */}
                        {index2 === 0 && (
                          <td
                            rowSpan={orders}
                            className="px-3 py-2 border-r border-slate-200 align-middle bg-white text-center"
                          >
                            <div className="flex gap-1.5 flex-col items-center justify-center min-w-[110px]">
                              <PrintChallanGroup
                                item={item}
                                orders={item?.orders}
                                refresh={refresh}
                              />
                              {!item?.orders?.every((o) => o?.isInvoiced) && (
                                <button
                                  className="bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 px-2.5 py-1 text-white text-[11px] font-bold rounded shadow-sm w-full transition-all active:scale-95 cursor-pointer flex items-center justify-center gap-1"
                                  onClick={() =>
                                    handlePrint(item?.orderIds, item?.orders)
                                  }
                                  disabled={invoiceLoading}
                                >
                                  <span>🧾</span>
                                  <span>
                                    {invoiceLoading
                                      ? "Loading..."
                                      : "Create Invoice"}
                                  </span>
                                </button>
                              )}
                            </div>
                          </td>
                        )}

                        {/* Images (Grouped) */}
                        {index2 === 0 && (
                          <td
                            rowSpan={orders}
                            className="px-3 py-2 whitespace-nowrap border-r border-slate-200 align-middle bg-white text-center"
                          >
                            <Images images={item?.images} />
                          </td>
                        )}

                        {/* Complete Action (Grouped) */}
                        {index2 === 0 && complete && (
                          <td
                            rowSpan={orders}
                            className="px-3 py-2 whitespace-nowrap align-middle bg-white text-center"
                          >
                            <div className="flex gap-2 flex-col items-center justify-center min-w-[90px]">
                              {isChallanCreated ? (
                                <button
                                  className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 px-3 py-1.5 text-white font-black rounded-lg w-full shadow text-xs transition active:scale-95 cursor-pointer flex items-center justify-center gap-1"
                                  onClick={() =>
                                    handleComplete(item?.orderIds, item?.orders)
                                  }
                                >
                                  <span>📷</span>
                                  <span>Complete</span>
                                </button>
                              ) : (
                                <button
                                  disabled={true}
                                  className="bg-slate-100 text-slate-400 px-2.5 py-1.5 font-bold rounded-lg w-full text-[11px] cursor-not-allowed border border-slate-200 select-none flex items-center justify-center gap-1"
                                  title="Please create Challan first"
                                >
                                  <span>🔒</span>
                                  <span>Complete</span>
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {(!data || data?.length === 0) && (
              <tr>
                <td
                  colSpan={11}
                  className="px-4 py-8 text-center text-gray-500 font-semibold bg-gray-50 text-sm"
                >
                  📦 No Dispatch Orders Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Complete Dispatch Modal with Attachment */}
      <CustomModal
        title="Complete Dispatch"
        open={showCompleteModal}
        setOpen={setShowCompleteModal}
        button={<></>}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="font-semibold text-xs">
              Select Orders to Complete:
            </label>
            {selectedOrders?.map((order, index) => (
              <div
                key={index}
                className="flex items-center gap-2 border border-gray-200 p-2 rounded text-xs bg-gray-50"
              >
                <Checkbox
                  size="small"
                  checked={selectedOrderIds?.includes(order._id)}
                  onChange={() => {
                    if (selectedOrderIds?.includes(order._id)) {
                      setSelectedOrderIds(
                        selectedOrderIds.filter((id) => id !== order._id)
                      );
                    } else {
                      setSelectedOrderIds([...selectedOrderIds, order._id]);
                    }
                  }}
                />
                <span className="font-semibold">
                  {order?.customer?.userName} - {order?.quantity}{" "}
                  {order?.product?.productName}
                </span>
              </div>
            ))}
          </div>

          {/* Note label on left, input field in middle, SAVE button on right */}
          <div className="flex items-center gap-2.5 w-full">
            <label className="font-bold text-sm whitespace-nowrap text-gray-900">
              Note
            </label>
            <input
              type="text"
              value={dispatchNote}
              onChange={(e) => setDispatchNote(e.target.value)}
              placeholder="Enter note..."
              className="flex-1 bg-white border-2 border-black py-2 px-3 rounded text-sm font-medium outline-none focus:border-blue-600 shadow-xs"
            />
            <button
              type="button"
              onClick={() => {
                if (!selectedOrderIds || selectedOrderIds?.length === 0) {
                  return toast.error("Please select at least one order");
                }
                if (!photoFile) {
                  return toast.error(
                    "⚠️ Attachment is REQUIRED! Please upload or capture photo before saving."
                  );
                }
                complete(selectedOrderIds, dispatchNote, photoFile);
                setShowCompleteModal(false);
                handleRemovePhoto();
              }}
              className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-extrabold px-6 py-2 rounded shadow transition active:scale-95 cursor-pointer whitespace-nowrap text-sm tracking-wider uppercase flex items-center justify-center"
            >
              SAVE
            </button>
          </div>

          {/* Photo Attachment Section */}
          <div className="flex flex-col gap-2 bg-yellow-50 p-3 rounded-lg border-2 border-amber-400">
            <label className="font-bold text-xs text-gray-800 flex items-center justify-between">
              <span className="flex items-center gap-1">
                <span>📸</span>
                <span>
                  Proof of Delivery / Challan Attachment{" "}
                  <span className="text-red-600 font-extrabold">
                    * (Required)
                  </span>
                  :
                </span>
              </span>
              {photoFile ? (
                <span className="text-[10px] text-green-700 bg-green-100 px-2 py-0.5 rounded font-bold">
                  ✓ Attached
                </span>
              ) : (
                <span className="text-[10px] text-red-700 bg-red-100 px-2 py-0.5 rounded font-bold">
                  Required
                </span>
              )}
            </label>

            {!photoFile && (
              <p className="text-[11px] text-red-600 font-semibold">
                ⚠️ Photo attachment is mandatory to complete and save.
              </p>
            )}

            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={cameraInputRef}
              onChange={handleFileChange}
              className="hidden"
            />
            <input
              type="file"
              accept="image/*"
              ref={galleryInputRef}
              onChange={handleFileChange}
              className="hidden"
            />

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-bold py-2.5 px-3 rounded-xl shadow text-xs flex items-center justify-center gap-1.5 transition active:scale-95 cursor-pointer"
              >
                <span className="text-sm">📷</span>
                <span>{photoFile ? "Retake Camera" : "Camera Photo"}</span>
              </button>
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-bold py-2.5 px-3 rounded-xl shadow text-xs flex items-center justify-center gap-1.5 transition active:scale-95 cursor-pointer"
              >
                <span className="text-sm">🖼️</span>
                <span>{photoFile ? "Change Gallery" : "Gallery / Files"}</span>
              </button>
            </div>

            {photoPreview && (
              <div className="mt-2 bg-white p-2 rounded-lg border border-gray-300 flex flex-col items-center">
                <img
                  src={photoPreview}
                  alt="Attachment Preview"
                  className="max-h-48 rounded object-contain w-full"
                />
                <button
                  type="button"
                  onClick={handleRemovePhoto}
                  className="mt-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-1 px-3 rounded shadow transition cursor-pointer"
                >
                  ✕ Remove Attachment
                </button>
              </div>
            )}
          </div>
        </div>
      </CustomModal>

      {/* Invoice Modal */}
      <CustomModal
        title="Print Invoice"
        open={showPrintModal}
        setOpen={setShowPrintModal}
        button={<></>}
      >
        <div className="flex flex-col gap-4">
          <ModalInput
            title="Note"
            value={printNote}
            onChange={(e) => setPrintNote(e.target.value)}
          />
          <ModalInput
            title="Weight"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
          />
        </div>
        <div className="flex items-end justify-end gap-2">
          <Button
            variant="contained"
            color="info"
            onClick={() => submitPrint(null)}
          >
            Save
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => submitPrint(true)}
          >
            Print
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => submitPrint(false)}
          >
            Whatsapp
          </Button>
        </div>
      </CustomModal>
    </div>
  );
};

export default DispatchTable;

// Combined Customer Group Challan (All items with single weight)
const PrintChallanGroup = ({ item, orders, refresh }) => {
  const [open, setOpen] = React.useState(false);
  const [weight, setWeight] = React.useState("");
  const [isPrinting, setIsPrinting] = React.useState(false);

  const isChallanCreated = orders?.some((o) => o?.isChallanCreated === true);
  const customerName =
    [...new Set(orders?.map((o) => o?.customer?.userName).filter(Boolean))].join(
      ", "
    ) || "Customer";
  const group = orders?.[0]?.group || orders?.[0]?.customer?.group?.name || "";
  const orderIds = item?.orderIds || orders?.map((o) => o._id) || [];

  const isTagOrder = (o) => {
    const prodGroup = (o?.product?.group || "").toLowerCase().trim();
    const ordGroup = (o?.group || "").toLowerCase().trim();
    const prodName = (o?.product?.productName || o?.description || "").toLowerCase().trim();
    return prodGroup === "tag" || ordGroup === "tag" || prodName.includes("tag");
  };

  const isTagItem = orders?.some(isTagOrder);
  const tagOrders = orders?.filter(isTagOrder) || [];
  const totalTagQty = tagOrders.reduce(
    (sum, o) => sum + (parseFloat(o?.quantity) || 0),
    0
  );
  const totalAllQty = orders?.reduce(
    (sum, o) => sum + (parseFloat(o?.quantity) || 0),
    0
  );

  // Pre-fill existing weight if available
  React.useEffect(() => {
    if (open) {
      let existingWeight = 0;
      for (const o of orders || []) {
        const w = parseFloat(o?.challanWeight || o?.weight || 0);
        if (w > 0) {
          existingWeight += w;
        }
      }
      if (existingWeight > 0) {
        setWeight(String(parseFloat(existingWeight.toFixed(3))));
      }
    }
  }, [open, orders]);

  // Customer proportional breakdown preview
  const customerBreakdown = React.useMemo(() => {
    if (!orders?.length) return [];
    const map = new Map();
    orders.forEach((ord) => {
      const custName = ord?.customer?.userName || "Customer";
      if (!map.has(custName)) {
        map.set(custName, { name: custName, tagQty: 0, totalQty: 0, orders: [] });
      }
      const entry = map.get(custName);
      const q = parseFloat(ord?.quantity) || 0;
      entry.totalQty += q;
      if (isTagOrder(ord)) {
        entry.tagQty += q;
      }
      entry.orders.push(ord);
    });

    const numWeight = parseFloat(weight);
    return Array.from(map.values()).map((c) => {
      let custWeight = 0;
      if (numWeight > 0) {
        if (totalTagQty > 0) {
          custWeight = Number(((c.tagQty * numWeight) / totalTagQty).toFixed(3));
        } else if (totalAllQty > 0) {
          custWeight = Number(((c.totalQty * numWeight) / totalAllQty).toFixed(3));
        }
      }
      return { ...c, calculatedWeight: custWeight };
    });
  }, [orders, weight, totalTagQty, totalAllQty]);

  const handleSaveOnly = async () => {
    const numWeight = parseFloat(weight);
    if (isTagItem && (!weight || isNaN(numWeight) || numWeight <= 0)) {
      return toast.error(
        "⚠️ Weight is REQUIRED for Tag category products! Please enter weight before saving."
      );
    }
    const finalWeight = !isNaN(numWeight) && numWeight > 0 ? numWeight : undefined;

    try {
      await createChallanApi(orderIds, finalWeight);
      toast.success("✅ Challan Saved Successfully!");
      setOpen(false);
      refresh?.();
      window.dispatchEvent(new Event("plabel-data-updated"));
      localStorage.setItem("plabel_last_sync", Date.now().toString());
    } catch (err) {
      console.error(err);
      toast.error("Error saving challan: " + (err.message || err));
    }
  };

  const handleBtPrintAndSave = async () => {
    const numWeight = parseFloat(weight);
    if (isTagItem && (!weight || isNaN(numWeight) || numWeight <= 0)) {
      return toast.error(
        "⚠️ Weight is REQUIRED for Tag category products! Please enter weight before printing."
      );
    }
    const finalWeight = !isNaN(numWeight) && numWeight > 0 ? numWeight : undefined;

    setIsPrinting(true);
    try {
      await createChallanApi(orderIds, finalWeight);
      const printData = buildChallanEscPos(orders, finalWeight, "challan");
      const dev = await autoConnectAndPrint(printData);
      toast.success(`✅ Saved & Printed to ${dev?.name || "Printer"}`);
      setOpen(false);
      refresh?.();
    } catch (err) {
      console.error(err);
      toast.error("Bluetooth print error: " + (err.message || err));
    } finally {
      setIsPrinting(false);
    }
  };

  const handlePdfPrintAndSave = async () => {
    const numWeight = parseFloat(weight);
    if (isTagItem && (!weight || isNaN(numWeight) || numWeight <= 0)) {
      return toast.error(
        "⚠️ Weight is REQUIRED for Tag category products! Please enter weight before opening PDF."
      );
    }
    const finalWeight = !isNaN(numWeight) && numWeight > 0 ? numWeight : 0;

    try {
      await createChallanApi(orderIds, finalWeight > 0 ? finalWeight : undefined);
      toast.success("✅ Challan Saved & Opening PDF!");
      setOpen(false);
      refresh?.();
      const orderParam = orders.map((o) => o._id).join(",");
      const custId = item?._id || "dispatch";
      window.open(
        `/admin/dispatch/challan-pdf/${encodeURIComponent(custId)}/${orderParam}/${finalWeight}`,
        "_blank"
      );
    } catch (err) {
      console.error(err);
      toast.error("Error saving challan: " + (err.message || err));
    }
  };

  if (isChallanCreated) {
    return (
      <button
        disabled={true}
        className="bg-slate-100 text-slate-400 px-2.5 py-1 rounded text-xs font-bold border border-slate-200 cursor-not-allowed select-none flex items-center justify-center gap-1 w-full"
        title="Challan already created"
      >
        <span>🔒</span>
        <span>Challan Created</span>
      </button>
    );
  }

  return (
    <div className="w-full">
      <button
        onClick={() => setOpen(true)}
        className="bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-2.5 py-1 rounded text-xs font-bold transition-all active:scale-95 cursor-pointer shadow-sm flex items-center justify-center gap-1 w-full"
      >
        <span>📄</span>
        <span>Challan</span>
      </button>

      {open && (
        <CustomModal
          title={`Create & Print Challan - ${customerName}${group ? ` (${group})` : ""}`}
          open={open}
          setOpen={setOpen}
          button={
            <div className="grid grid-cols-3 gap-2 w-full pt-2">
              <Button
                variant="contained"
                onClick={handleSaveOnly}
                disabled={isPrinting}
                style={{
                  textTransform: "none",
                  fontWeight: "bold",
                  backgroundColor: "#2563eb",
                  color: "#fff",
                }}
              >
                💾 Save
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={handleBtPrintAndSave}
                disabled={isPrinting}
                style={{ textTransform: "none", fontWeight: "bold" }}
              >
                {isPrinting ? "Printing..." : "⚡ BT Print (58mm)"}
              </Button>

              <Button
                variant="contained"
                onClick={handlePdfPrintAndSave}
                disabled={isPrinting}
                style={{
                  textTransform: "none",
                  fontWeight: "bold",
                  backgroundColor: "#16a34a",
                  color: "#fff",
                }}
              >
                📄 PDF Print (3.5"x6")
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-3">
            <div className="bg-gray-50 p-2.5 rounded border border-gray-200">
              <div className="font-bold text-xs text-gray-700 mb-1.5 flex justify-between items-center">
                <span>Items ({orders?.length || 0}):</span>
                {isTagItem ? (
                  <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-[11px] font-bold">
                    Tag: {totalTagQty.toLocaleString()} pcs (Weight Required)
                  </span>
                ) : (
                  <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[11px] font-medium">
                    Non-Tag Category
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1 max-h-40 overflow-y-auto">
                {orders?.map((ord, idx) => {
                  const isTag = isTagOrder(ord);
                  return (
                    <div
                      key={idx}
                      className="flex justify-between items-center text-xs bg-white px-2 py-1 rounded border border-gray-100"
                    >
                      <span className="font-semibold text-gray-800">
                        {idx + 1}. {ord?.product?.productName || ord?.description}
                        <span className="text-[10px] text-gray-500 font-normal ml-1">
                          ({ord?.customer?.userName || "Customer"})
                        </span>
                      </span>
                      <div className="flex items-center gap-1.5">
                        {isTag && (
                          <span className="text-[9px] font-bold px-1.5 py-0.2 bg-amber-100 text-amber-800 rounded">
                            Tag
                          </span>
                        )}
                        <span className="font-bold text-blue-600">
                          Qty: {ord?.quantity}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {isTagItem && (
              <div className="flex flex-col gap-2 p-3 rounded-lg border-2 bg-yellow-50 border-amber-400">
                <label className="text-xs font-bold text-gray-800 flex items-center justify-between">
                  <span>
                    Weight (kg) - For all items{" "}
                    <span className="text-red-600 font-extrabold">* (Required)</span>:
                  </span>
                  {weight && parseFloat(weight) > 0 ? (
                    <span className="text-[10px] text-green-700 bg-green-100 px-2 py-0.5 rounded font-bold">
                      ✓ Valid
                    </span>
                  ) : (
                    <span className="text-[10px] text-red-700 bg-red-100 px-2 py-0.5 rounded font-bold">
                      Required
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  autoFocus
                  required={true}
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  placeholder="Enter weight in kg..."
                  className="w-full border border-gray-300 focus:border-blue-600 p-2 rounded text-sm font-bold outline-none bg-white shadow-sm"
                />

                {/* Proportional Division Breakdown Preview */}
                {weight && parseFloat(weight) > 0 && customerBreakdown.length > 1 && (
                  <div className="mt-1 bg-white p-2 rounded border border-amber-300 text-xs flex flex-col gap-1">
                    <span className="font-bold text-gray-700 text-[11px]">
                      📊 Proportional Weight Distribution:
                    </span>
                    {customerBreakdown.map((cb, cIdx) => (
                      <div
                        key={cIdx}
                        className="flex justify-between items-center text-[11px] bg-amber-50/60 px-2 py-1 rounded"
                      >
                        <span className="font-semibold text-gray-800">
                          {cb.name}{" "}
                          <span className="text-[10px] text-gray-500">
                            ({cb.tagQty > 0 ? `${cb.tagQty} Tag pcs` : `${cb.totalQty} pcs`})
                          </span>
                        </span>
                        <span className="font-bold text-emerald-800 font-mono">
                          {cb.calculatedWeight} kg
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {(!weight || parseFloat(weight) <= 0) && (
                  <span className="text-[11px] text-red-600 font-semibold">
                    ⚠️ Weight is mandatory for Tag products to save and print challan.
                  </span>
                )}
              </div>
            )}
          </div>
        </CustomModal>
      )}
    </div>
  );
};

const Images = ({ images }) => {
  const [open, setOpen] = React.useState(false);
  const count = images?.length || 0;
  return (
    <div>
      {count > 0 ? (
        <button
          onClick={() => setOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 rounded text-xs font-bold shadow-sm transition active:scale-95 cursor-pointer flex items-center justify-center gap-1 mx-auto"
        >
          <span>🖼️</span>
          <span>View ({count})</span>
        </button>
      ) : (
        <span className="text-gray-400 text-xs italic">-</span>
      )}
      <CustomModal open={open} setOpen={setOpen} button={<></>}>
        <div className="grid grid-cols-2 gap-3 p-2">
          {images?.map((item, index) => (
            <a
              key={index}
              href={item}
              target="_blank"
              rel="noreferrer"
              className="block border rounded-lg p-1 hover:shadow-lg transition"
            >
              <img
                src={item}
                alt="attachment"
                className="h-72 w-full object-contain rounded"
              />
            </a>
          ))}
        </div>
      </CustomModal>
    </div>
  );
};
